import { CollectionRef } from "@/types/collection"
import { InspirationRef } from "@/types/entity"
import {
  MediaCollection,
  MediaFetchPerPageSize,
  MediaFile,
  MediaGuide,
  MediaLink,
} from "@/types/media"
import { TripRef } from "@/types/trip"
import { TripsTiming } from "@/types/trips"

import { PagingMeta } from "@/lib/paging"
import { baseApi } from "./base-api"
import { itineraryApi } from "./itinerary-api"
import { tripsApi } from "./trips-api"

export interface GetMediaGuidesResponse {
  data: MediaGuide[]
  meta: PagingMeta
}

export interface GetMediaCollectionsResponse {
  data: MediaCollection[]
  meta: PagingMeta
}

export interface GetMediaFilesResponse {
  data: MediaFile[]
  meta: PagingMeta
}

export interface GetMediaVideosResponse {
  data: MediaLink[]
  meta: PagingMeta
}

export interface GetMediaLinksResponse {
  data: MediaLink[]
  meta: PagingMeta
}

export interface GetMediaArgs {
  tripRef?: TripRef
  page?: number
  perPage?: number
}

export const mediaApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTripRefsForMedia: builder.query<
      TripRef[],
      { sourceRef: InspirationRef | CollectionRef; timing?: TripsTiming }
    >({
      query: ({ sourceRef, timing }) => {
        const params = new URLSearchParams()
        if (timing) params.append("timing", timing)
        const queryString = params.toString()
        const url = queryString
          ? `/api/media/fetch_ref/${sourceRef}?${queryString}`
          : `/api/media/fetch_ref/${sourceRef}`
        return { url }
      },
      transformResponse: (response: { trips_ref: TripRef[] }) =>
        response.trips_ref,
      providesTags: (_result, _error, { sourceRef }) => [
        { type: "MediaTripRefs", id: sourceRef },
      ],
    }),
    getMediaGuides: builder.query<GetMediaGuidesResponse, GetMediaArgs>({
      query: ({ tripRef, page = 1, perPage = MediaFetchPerPageSize }) => {
        const params = new URLSearchParams()
        if (tripRef) params.append("tripRef", tripRef)
        params.append("page", page.toString())
        params.append("per_page", perPage.toString())
        const queryString = params.toString()
        const url = `/api/media/guides?${queryString}`
        return { url }
      },
      providesTags: (_result, _error, { tripRef }) => [
        { type: "MediaGuides", id: tripRef || "all" },
      ],
      serializeQueryArgs: ({ queryArgs }) => {
        return `guides-${queryArgs.tripRef || "all"}`
      },
      merge: (current, incoming, { arg }) => {
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            data: [...current.data, ...incoming.data],
          }
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    getMediaCollections: builder.query<
      GetMediaCollectionsResponse,
      GetMediaArgs
    >({
      query: ({ tripRef, page = 1, perPage = MediaFetchPerPageSize }) => {
        const params = new URLSearchParams()
        if (tripRef) params.append("tripRef", tripRef)
        params.append("page", page.toString())
        params.append("per_page", perPage.toString())
        const queryString = params.toString()
        const url = `/api/media/collections?${queryString}`
        return { url }
      },
      providesTags: (_result, _error, { tripRef }) => [
        { type: "MediaCollections", id: tripRef || "all" },
      ],
      serializeQueryArgs: ({ queryArgs }) => {
        return `collections-${queryArgs.tripRef || "all"}`
      },
      merge: (current, incoming, { arg }) => {
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            data: [...current.data, ...incoming.data],
          }
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    getMediaFiles: builder.query<GetMediaFilesResponse, GetMediaArgs>({
      query: ({ tripRef, page = 1, perPage = MediaFetchPerPageSize }) => {
        const params = new URLSearchParams()
        if (tripRef) params.append("tripRef", tripRef)
        params.append("page", page.toString())
        params.append("per_page", perPage.toString())
        const queryString = params.toString()
        const url = `/api/media/files?${queryString}`
        return { url }
      },
      providesTags: (_result, _error, { tripRef }) => [
        { type: "MediaFiles", id: tripRef || "all" },
      ],
      serializeQueryArgs: ({ queryArgs }) => {
        return `files-${queryArgs.tripRef || "all"}`
      },
      merge: (current, incoming, { arg }) => {
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            data: [...current.data, ...incoming.data],
          }
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    getMediaVideos: builder.query<GetMediaVideosResponse, GetMediaArgs>({
      query: ({ tripRef, page = 1, perPage = MediaFetchPerPageSize }) => {
        const params = new URLSearchParams()
        if (tripRef) params.append("tripRef", tripRef)
        params.append("page", page.toString())
        params.append("per_page", perPage.toString())
        const queryString = params.toString()
        const url = `/api/media/videos?${queryString}`
        return { url }
      },
      providesTags: (_result, _error, { tripRef }) => [
        { type: "MediaVideos", id: tripRef || "all" },
      ],
      serializeQueryArgs: ({ queryArgs }) => {
        return `videos-${queryArgs.tripRef || "all"}`
      },
      merge: (current, incoming, { arg }) => {
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            data: [...current.data, ...incoming.data],
          }
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    getMediaLinks: builder.query<GetMediaLinksResponse, GetMediaArgs>({
      query: ({ tripRef, page = 1, perPage = MediaFetchPerPageSize }) => {
        const params = new URLSearchParams()
        if (tripRef) params.append("tripRef", tripRef)
        params.append("page", page.toString())
        params.append("per_page", perPage.toString())
        const queryString = params.toString()
        const url = `/api/media/links?${queryString}`
        return { url }
      },
      providesTags: (_result, _error, { tripRef }) => [
        { type: "MediaLinks", id: tripRef || "all" },
      ],
      serializeQueryArgs: ({ queryArgs }) => {
        return `links-${queryArgs.tripRef || "all"}`
      },
      merge: (current, incoming, { arg }) => {
        if (arg.page && arg.page > 1 && current) {
          return {
            ...incoming,
            data: [...current.data, ...incoming.data],
          }
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    uploadMediaFile: builder.mutation<
      { media: MediaFile },
      { tripRef: TripRef; file: File; eventId?: number }
    >({
      query: ({ tripRef, file, eventId }) => {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("type", "item-image")
        formData.append("tripRef", tripRef)
        if (eventId != null) {
          formData.append("event_id", eventId.toString())
        }
        return {
          url: "/api/media/upload_file",
          method: "POST",
          data: formData,
          headers: {
            "content-type": "multipart/form-data",
          },
        }
      },
      async onQueryStarted({ tripRef, eventId }, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled
          if (eventId != null && result.data.media) {
            dispatch(
              itineraryApi.util.updateQueryData(
                "getItinerary",
                { tripRef },
                (draft) => {
                  if (!draft) return
                  const event = draft.events.find((e) => e.id === eventId)
                  if (!event) return
                  event.media = [...(event.media || []), result.data.media]
                }
              )
            )
          }
          dispatch(
            tripsApi.util.invalidateTags([{ type: "TripCounts", id: tripRef }])
          )
        } catch {
          // Ignore errors
        }
      },
      invalidatesTags: (_result, _error, { tripRef, eventId }) =>
        eventId != null ? [] : [{ type: "MediaFiles", id: tripRef }],
    }),
    uploadMediaLink: builder.mutation<
      { media: MediaLink | MediaGuide | MediaCollection },
      { tripRef: TripRef; url: string }
    >({
      query: ({ tripRef, url }) => ({
        url: "/api/media/upload_link",
        method: "POST",
        data: {
          tripRef: tripRef,
          link: url,
        },
      }),
      async onQueryStarted({ tripRef }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(
            tripsApi.util.invalidateTags([{ type: "TripCounts", id: tripRef }])
          )
        } catch {
          // Ignore errors
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "MediaGuides", id: tripRef },
        { type: "MediaCollections", id: tripRef },
        { type: "MediaVideos", id: tripRef },
        { type: "MediaLinks", id: tripRef },
      ],
    }),
    addMediaGuide: builder.mutation<
      { media: MediaLink },
      { tripRef: TripRef | undefined; inspirationRef: InspirationRef }
    >({
      query: ({ tripRef, inspirationRef }) => ({
        url: "/api/media/add_guide",
        method: "POST",
        data: {
          inspirationRef: inspirationRef,
          tripRef: tripRef,
        },
      }),
      async onQueryStarted(
        { tripRef, inspirationRef },
        { dispatch, queryFulfilled }
      ) {
        // Optimistically update getTripRefsForMedia cache
        let patchResult: { undo: () => void } | undefined
        if (tripRef) {
          try {
            patchResult = dispatch(
              mediaApi.util.updateQueryData(
                "getTripRefsForMedia",
                { sourceRef: inspirationRef },
                (draft) => {
                  if (!draft.includes(tripRef)) {
                    draft.push(tripRef)
                  }
                }
              )
            )
          } catch {
            // Cache entry doesn't exist yet, skip optimistic update
          }
        }

        try {
          await queryFulfilled
          if (tripRef) {
            dispatch(
              tripsApi.util.invalidateTags([
                { type: "TripCounts", id: tripRef },
              ])
            )
          }
        } catch {
          // Revert optimistic update on failure
          if (patchResult) {
            patchResult.undo()
          }
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "MediaGuides", id: tripRef || "all" },
        "MediaTripRefs",
      ],
    }),
    addMediaCollection: builder.mutation<
      { media: MediaLink },
      { tripRef: TripRef | undefined; collectionRef: CollectionRef }
    >({
      query: ({ tripRef, collectionRef }) => ({
        url: "/api/media/add_collection",
        method: "POST",
        data: {
          collectionRef: collectionRef,
          tripRef: tripRef,
        },
      }),
      async onQueryStarted(
        { tripRef, collectionRef },
        { dispatch, queryFulfilled }
      ) {
        // Optimistically update getTripRefsForMedia cache
        let patchResult: { undo: () => void } | undefined
        if (tripRef) {
          try {
            patchResult = dispatch(
              mediaApi.util.updateQueryData(
                "getTripRefsForMedia",
                { sourceRef: collectionRef },
                (draft) => {
                  if (!draft.includes(tripRef)) {
                    draft.push(tripRef)
                  }
                }
              )
            )
          } catch {
            // Cache entry doesn't exist yet, skip optimistic update
          }
        }

        try {
          await queryFulfilled
          if (tripRef) {
            dispatch(
              tripsApi.util.invalidateTags([
                { type: "TripCounts", id: tripRef },
              ])
            )
          }
        } catch {
          // Revert optimistic update on failure
          if (patchResult) {
            patchResult.undo()
          }
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "MediaCollections", id: tripRef || "all" },
        "MediaTripRefs",
      ],
    }),
    removeMedia: builder.mutation<
      boolean,
      { tripRef: TripRef; mediaRef: string; eventId?: number }
    >({
      query: ({ tripRef, mediaRef }) => ({
        url: `/api/media/${mediaRef}/remove`,
        method: "POST",
        data: {
          tripRef: tripRef,
        },
      }),
      async onQueryStarted(
        { tripRef, mediaRef, eventId },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled
          if (eventId != null) {
            dispatch(
              itineraryApi.util.updateQueryData(
                "getItinerary",
                { tripRef },
                (draft) => {
                  if (!draft) return
                  const event = draft.events.find((e) => e.id === eventId)
                  if (!event?.media) return
                  event.media = event.media.filter((m) => m.ref !== mediaRef)
                  if (event.properties.cover_media_ref === mediaRef) {
                    const { cover_media_ref: _, ...properties } =
                      event.properties
                    event.properties = properties
                  }
                }
              )
            )
          }
          dispatch(
            tripsApi.util.invalidateTags([{ type: "TripCounts", id: tripRef }])
          )
        } catch {
          // Ignore errors
        }
      },
      invalidatesTags: (_result, _error, { tripRef, eventId }) =>
        eventId != null
          ? []
          : [
              { type: "MediaGuides", id: tripRef },
              { type: "MediaCollections", id: tripRef },
              { type: "MediaFiles", id: tripRef },
              { type: "MediaVideos", id: tripRef },
              { type: "MediaLinks", id: tripRef },
            ],
    }),
    removeGuideForTrip: builder.mutation<
      boolean,
      { tripRef: TripRef; inspirationRef: InspirationRef }
    >({
      query: ({ tripRef, inspirationRef }) => ({
        url: `/api/media/remove/inspiration/${inspirationRef}`,
        method: "POST",
        data: {
          tripRef: tripRef,
        },
      }),
      async onQueryStarted(
        { tripRef, inspirationRef },
        { dispatch, queryFulfilled }
      ) {
        // Optimistically update getTripRefsForMedia cache
        let patchResult: { undo: () => void } | undefined
        try {
          patchResult = dispatch(
            mediaApi.util.updateQueryData(
              "getTripRefsForMedia",
              { sourceRef: inspirationRef },
              (draft) => {
                const index = draft.indexOf(tripRef)
                if (index !== -1) {
                  draft.splice(index, 1)
                }
              }
            )
          )
        } catch {
          // Cache entry doesn't exist yet, skip optimistic update
        }

        try {
          await queryFulfilled
          dispatch(
            tripsApi.util.invalidateTags([{ type: "TripCounts", id: tripRef }])
          )
        } catch {
          // Revert optimistic update on failure
          if (patchResult) {
            patchResult.undo()
          }
        }
      },
      invalidatesTags: (_result, _error, { tripRef, inspirationRef }) => [
        { type: "MediaGuides", id: tripRef },
        { type: "MediaTripRefs", id: inspirationRef },
      ],
    }),
    removeCollectionForTrip: builder.mutation<
      boolean,
      { tripRef: TripRef; collectionRef: CollectionRef }
    >({
      query: ({ tripRef, collectionRef }) => ({
        url: `/api/media/remove/collection/${collectionRef}`,
        method: "POST",
        data: {
          tripRef: tripRef,
        },
      }),
      async onQueryStarted(
        { tripRef, collectionRef },
        { dispatch, queryFulfilled }
      ) {
        // Optimistically update getTripRefsForMedia cache
        let patchResult: { undo: () => void } | undefined
        try {
          patchResult = dispatch(
            mediaApi.util.updateQueryData(
              "getTripRefsForMedia",
              { sourceRef: collectionRef },
              (draft) => {
                const index = draft.indexOf(tripRef)
                if (index !== -1) {
                  draft.splice(index, 1)
                }
              }
            )
          )
        } catch {
          // Cache entry doesn't exist yet, skip optimistic update
        }

        try {
          await queryFulfilled
          dispatch(
            tripsApi.util.invalidateTags([{ type: "TripCounts", id: tripRef }])
          )
        } catch {
          // Revert optimistic update on failure
          if (patchResult) {
            patchResult.undo()
          }
        }
      },
      invalidatesTags: (_result, _error, { tripRef, collectionRef }) => [
        { type: "MediaCollections", id: tripRef },
        { type: "MediaTripRefs", id: collectionRef },
      ],
    }),
  }),
})

export const {
  useGetTripRefsForMediaQuery,
  useLazyGetTripRefsForMediaQuery,
  useGetMediaGuidesQuery,
  useLazyGetMediaGuidesQuery,
  useGetMediaCollectionsQuery,
  useLazyGetMediaCollectionsQuery,
  useGetMediaFilesQuery,
  useLazyGetMediaFilesQuery,
  useGetMediaVideosQuery,
  useLazyGetMediaVideosQuery,
  useGetMediaLinksQuery,
  useLazyGetMediaLinksQuery,
  useUploadMediaFileMutation,
  useUploadMediaLinkMutation,
  useAddMediaGuideMutation,
  useAddMediaCollectionMutation,
  useRemoveMediaMutation,
  useRemoveGuideForTripMutation,
  useRemoveCollectionForTripMutation,
} = mediaApi
